/**
 * Settings → Text size: one factor on the root font size (the `--ui-scale` CSS variable)
 * scales everything sized in rem. Pixel math that must match the visuals (indents, drag
 * projection) goes through `scaled` / `uiScale`.
 */
export const TEXT_SIZES = [
  { key: 'xs', label: 'Smallest', scale: 0.875 },
  { key: 's', label: 'Small', scale: 0.9375 },
  { key: 'm', label: 'Default', scale: 1 },
  { key: 'l', label: 'Large', scale: 1.125 },
  { key: 'xl', label: 'Largest', scale: 1.25 },
] as const
export type TextSize = (typeof TEXT_SIZES)[number]['key']

const KEY = 'tz.uiScale'
let current = 1

/** Apply a text size now (and remember it for the next launch's first paint). */
export function applyTextSize(size: string | undefined) {
  const scale = TEXT_SIZES.find((t) => t.key === size)?.scale ?? 1
  current = scale
  document.documentElement.style.setProperty('--ui-scale', String(scale))
  try {
    localStorage.setItem(KEY, String(scale))
  } catch {
    // private mode: fine, it's re-applied from settings
  }
}

/** Before the first render: last used size, so the app doesn't jump once settings load. */
export function applyCachedTextSize() {
  try {
    const v = Number(localStorage.getItem(KEY))
    if (v > 0.5 && v < 2) {
      current = v
      document.documentElement.style.setProperty('--ui-scale', String(v))
    }
  } catch {
    // ignore
  }
}

export const uiScale = () => current

/** A CSS length for `px` at the default size, scaled with the text size. */
export const scaled = (px: number) => `calc(${px}px * var(--ui-scale, 1))`
